import { AnalyticsEvent } from 'utils/analytics'
import { FC } from 'react'
import { GRAY_300, GRAY_700, GREEN_100, GREEN_700 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { MUIButton } from 'components/common/MUIButton'
import { useEmbedClose } from 'utils/embed/useEmbedClose'
import { useEmbedCrm } from 'utils/embedSession/useEmbedCrm'
import { useTranslation } from 'react-i18next'

/**
 * How the viewer reached the cancelled state: JUST_CANCELLED right after an in-page cancellation
 * (success tone), ALREADY_CANCELLED when deep-linking to an order that was cancelled earlier
 * (informational tone, e.g. from a stale Propstack tile).
 */
export type EmbedOrderCancelledVariant = 'JUST_CANCELLED' | 'ALREADY_CANCELLED'

interface Props {
  variant: EmbedOrderCancelledVariant
}

/**
 * Terminal "order cancelled" screen of the Propstack embed order-actions page (ISSUE-3147).
 * Replaces the misleading generic property error that used to render once an order turned
 * CANCELLED. Offers a Close button that posts BACKBONE_CLOSE to the Propstack parent page
 * (same contract as the ISSUE-3101 order-confirmation Close); the contract forbids a wildcard
 * targetOrigin, so the button is hidden when no allowlisted parent origin can be resolved.
 */
export const EmbedOrderCancelled: FC<Props> = ({ variant }) => {
  const { t } = useTranslation(['embed'])
  const { crmName } = useEmbedCrm()

  const { canClose, handleClose } = useEmbedClose(
    AnalyticsEvent.EMBED_ORDER_CANCELLED_CLOSE_CLICKED,
    AnalyticsEvent.EMBED_ORDER_CANCELLED_CLOSE_UNAVAILABLE,
    { variant }
  )

  const isJustCancelled = variant === 'JUST_CANCELLED'
  const copyKey = isJustCancelled ? 'status_card.cancelled' : 'status_card.already_cancelled'

  return (
    <Stack
      gap={3}
      alignItems="center"
      justifyContent="center"
      data-testid="embed-order-cancelled"
      sx={{ height: '100%', minHeight: '60vh', padding: 4, textAlign: 'center' }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ width: 96, height: 96, borderRadius: '50%', backgroundColor: isJustCancelled ? GREEN_100 : GRAY_300 }}
      >
        {isJustCancelled
          ? <CheckCircleOutlinedIcon sx={{ fontSize: 48, color: GREEN_700 }} />
          : <InfoOutlinedIcon sx={{ fontSize: 48, color: GRAY_700 }} />
        }
      </Stack>

      <Stack gap={1} alignItems="center">
        <Typography variant="h5">{t(`${copyKey}.title`)}</Typography>
        <Typography variant="body1" color={GRAY_700} sx={{ maxWidth: 480 }}>
          {t(`${copyKey}.message`)}
        </Typography>
      </Stack>

      {canClose
        ? (
          <MUIButton type="defaultPrimary" variant="contained" onClick={handleClose}>
            {t('status_card.cancelled.close')}
          </MUIButton>
        )
        : (
          // No resolvable parent origin (e.g. Firefox with a suppressed referrer): the screen must
          // not be a dead end, so tell the broker how to leave instead of offering nothing.
          <Typography variant="body2" color={GRAY_700}>
            {t('status_card.cancelled.close_fallback_crm', { crm: crmName })}
          </Typography>
        )
      }
    </Stack>
  )
}

export default EmbedOrderCancelled
